import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pin, PinOff, Trash2, ArrowUp, ArrowDown, RefreshCw, Plus, Star } from "lucide-react";
import { http } from "@/lib/api";

const COUNTRY_CODES = ["CA", "GB", "AU", "DE", "AE", "SG", "US", "IE", "NZ", "NL", "PT"];

export default function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", role: "", city: "", flag: "", country_code: "", img: "", quote: "" });
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const { data } = await http.get("/testimonials");
      setItems(data.testimonials || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = async (id, updates) => {
    try {
      const { data } = await http.patch(`/testimonials/${id}`, updates);
      setItems((cur) => cur.map((t) => (t.id === id ? data : t)).sort((a, b) => (b.pinned - a.pinned) || (a.order - b.order)));
    } catch (e) { toast.error("Update failed"); }
  };

  const move = async (id, dir) => {
    const idx = items.findIndex((t) => t.id === id);
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    const next = [...items];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next);
    try {
      await http.post("/testimonials/reorder", { ids: next.map((t) => t.id) });
    } catch (e) { toast.error("Reorder failed"); load(); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await http.delete(`/testimonials/${id}`);
      setItems((cur) => cur.filter((t) => t.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.quote.trim()) { toast.error("Name and quote required"); return; }
    try {
      const { data } = await http.post("/testimonials", form);
      setItems((cur) => [...cur, data]);
      setForm({ name: "", role: "", city: "", flag: "", country_code: "", img: "", quote: "" });
      setShowAdd(false);
      toast.success("Testimonial added");
    } catch (e) { toast.error("Add failed"); }
  };

  const importGoogle = async () => {
    setRefreshing(true);
    try {
      const { data } = await http.post("/reviews/google/refresh", {});
      toast.success(`Imported ${data.imported} new Google reviews (of ${data.total_reviews_seen} seen)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Google refresh failed");
    } finally { setRefreshing(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm mb-6" data-testid="testimonials-admin">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-[#C9A961]" />
          <div className="text-xs uppercase tracking-widest text-slate-500">Testimonials · pinned first</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={importGoogle}
            disabled={refreshing}
            data-testid="google-refresh-btn"
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Fetching…" : "Import Google reviews"}
          </button>
          <button
            onClick={() => setShowAdd((s) => !s)}
            data-testid="testimonial-add-toggle"
            className="inline-flex items-center gap-1 rounded-lg bg-[#0B1E3F] text-white px-3 py-1.5 text-xs font-semibold"
          >
            <Plus className="w-3.5 h-3.5" /> {showAdd ? "Cancel" : "Add"}
          </button>
        </div>
      </div>

      {showAdd && (
        <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4 p-3 bg-slate-50 rounded-xl">
          <input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} placeholder="Full name *" className="text-sm border border-slate-200 rounded-lg px-3 py-2" />
          <input value={form.role} onChange={(e)=>setForm({...form,role:e.target.value})} placeholder="Role" className="text-sm border border-slate-200 rounded-lg px-3 py-2" />
          <input value={form.city} onChange={(e)=>setForm({...form,city:e.target.value})} placeholder="City / route (e.g. Mumbai → Berlin)" className="text-sm border border-slate-200 rounded-lg px-3 py-2" />
          <input value={form.flag} onChange={(e)=>setForm({...form,flag:e.target.value})} placeholder="Flag emoji 🇮🇳" className="text-sm border border-slate-200 rounded-lg px-3 py-2" />
          <select value={form.country_code} onChange={(e)=>setForm({...form,country_code:e.target.value})} className="text-sm border border-slate-200 rounded-lg px-3 py-2">
            <option value="">Destination country code…</option>
            {COUNTRY_CODES.map((c)=><option key={c}>{c}</option>)}
          </select>
          <input value={form.img} onChange={(e)=>setForm({...form,img:e.target.value})} placeholder="Portrait image URL" className="text-sm border border-slate-200 rounded-lg px-3 py-2" />
          <textarea value={form.quote} onChange={(e)=>setForm({...form,quote:e.target.value})} placeholder="Quote *" rows={2} className="text-sm border border-slate-200 rounded-lg px-3 py-2 md:col-span-2" />
          <button type="submit" data-testid="testimonial-save-btn" className="md:col-span-2 rounded-lg bg-[#0B1E3F] text-white text-xs font-semibold py-2">Save</button>
        </form>
      )}

      <div className="space-y-2 max-h-[480px] overflow-y-auto thin-scroll">
        {loading && <div className="text-xs text-slate-400 text-center py-6">Loading…</div>}
        {!loading && items.length === 0 && <div className="text-xs text-slate-400 text-center py-6">No testimonials yet.</div>}
        {items.map((t, i) => (
          <div key={t.id} data-testid={`testimonial-row-${i}`} className={`flex items-center gap-3 border rounded-xl p-2.5 ${t.pinned ? "border-[#C9A961]/60 bg-[#FFFAF0]" : "border-slate-100 bg-white"}`}>
            <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">
                {t.flag} {t.name}
                {t.source === "google" && <span className="ml-1 text-[9px] uppercase tracking-widest text-blue-600 font-bold">google</span>}
                {t.pinned && <span className="ml-1 text-[9px] uppercase tracking-widest text-[#C9A961] font-bold">pinned</span>}
              </div>
              <div className="text-[11px] text-slate-500 truncate">{t.role} · {t.city}</div>
              <div className="text-xs text-slate-600 truncate italic">"{t.quote}"</div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => move(t.id, -1)} className="p-1 text-slate-400 hover:text-[#0B1E3F]" title="Move up" data-testid={`testimonial-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></button>
              <button onClick={() => move(t.id, 1)}  className="p-1 text-slate-400 hover:text-[#0B1E3F]" title="Move down" data-testid={`testimonial-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></button>
              <button
                onClick={() => patch(t.id, { pinned: !t.pinned })}
                className={`p-1 ${t.pinned ? "text-[#C9A961]" : "text-slate-400 hover:text-[#C9A961]"}`}
                title={t.pinned ? "Unpin" : "Pin"}
                data-testid={`testimonial-pin-${i}`}
              >
                {t.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => remove(t.id)} className="p-1 text-slate-400 hover:text-red-600" title="Delete" data-testid={`testimonial-delete-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
